import { useTranslation } from 'react-i18next'

import { usePermissions } from '@percent/admin-dashboard/common/hooks/usePermissions/usePermissions'
import { useRolesAccess } from '@percent/admin-dashboard/common/hooks/useRolesAccess/useRolesAccess'
import {
  POSTHOG_FEATURE_FLAGS,
  useFeatureFlagsReady,
  usePostHogFeatureFlag
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

export const ACCOUNT_TAB_ROUTES = {
  members: '/accounts/members',
  groups: '/accounts/groups',
  roles: '/accounts/roles',
  services: '/accounts/services'
} as const

export type AccountTabRoute = typeof ACCOUNT_TAB_ROUTES[keyof typeof ACCOUNT_TAB_ROUTES]

type AccountTab = { route: AccountTabRoute; label: string; isAvailable: boolean; dependsOnFlags: boolean }

/** The accounts page's tabs the viewer can open, the one to land on, and whether a route is closed to them. */
export function useAccountTabs() {
  const { t } = useTranslation()
  const { userHasPermissions } = usePermissions()
  const hasRolesAccess = useRolesAccess()
  const isServicesEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_SERVICES)
  const featureFlagsReady = useFeatureFlagsReady()
  // In only through the services flag, so Services is the one tab that is theirs
  const servicesOnly = isServicesEnabled && !userHasPermissions('accounts:can_list')

  const allTabs: AccountTab[] = [
    {
      route: ACCOUNT_TAB_ROUTES.members,
      label: t('typography.members'),
      isAvailable: !servicesOnly,
      dependsOnFlags: true
    },
    {
      route: ACCOUNT_TAB_ROUTES.groups,
      label: t('typography.groups'),
      isAvailable: !servicesOnly && userHasPermissions('groups:can_list'),
      dependsOnFlags: false
    },
    {
      route: ACCOUNT_TAB_ROUTES.roles,
      label: t('typography.roles'),
      isAvailable: !servicesOnly && hasRolesAccess,
      dependsOnFlags: true
    },
    {
      route: ACCOUNT_TAB_ROUTES.services,
      label: t('typography.services'),
      isAvailable: isServicesEnabled,
      dependsOnFlags: true
    }
  ]
  const tabs = allTabs.filter(tab => tab.isAvailable)

  // Waits for flags before closing a flag's tab, so a direct link isn't redirected before the flag has loaded
  const isRouteClosed = (pathname: string) =>
    allTabs.some(tab => tab.route === pathname && !tab.isAvailable && (featureFlagsReady || !tab.dependsOnFlags))

  return { tabs, defaultRoute: tabs[0]?.route ?? ACCOUNT_TAB_ROUTES.members, isRouteClosed }
}
